import { Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { TaskQuestionType, ContractChallengeType } from '../../contract_enums';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import ModalProgress from '../../../../Components/Progress/Modal/ModalProgress';
import QuestionChallenge from './QuestionChallenge';
import { useContext } from 'react';
import { UserContext } from '../../../../Contexts/UserContext';
import { useMemo } from 'react';
import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';

import { invoiceNumberName } from '../../../../Configs/general';
import { yupResolver } from '@hookform/resolvers/yup';

const SCHEMA_CONFIGS = {
  [TaskQuestionType.RecentInvoice]: {
    value: yup
      .string()
      .matches(/^[a-zA-Z0-9]*$/, 'El valor debe ser alfanumérico')
      .max(20, 'Debe tener 20 caracteres o menos.')
      .required('Ingresa la respuesta')
  },
  [TaskQuestionType.PhoneValidation]: {
    phone: yup
      .string()
      .trim()
      .length(10, 'Debe tener 10 dígitos')
      .required('Ingresa un teléfono'),
    phoneCountryCode: yup
      .string()
      .trim()
      .max(3, 'Debe tener máximo 3 dígitos')
      .default('57')
      .required('Debe ser válido')
  },
  [TaskQuestionType.PassKey]: {
    value: yup
      .string()
      .trim()
      .length(6, 'Debe tener 6 caracteres')
      .required('Ingresa un código')
  }
};

const DEFAULT_VALUE_CONFIGS = {
  [TaskQuestionType.RecentInvoice]: () => ({
    value: ''
  }),
  [TaskQuestionType.PhoneValidation]: user => ({
    phone: user.phone || '',
    phoneCountryCode: user.phoneCountryCode || '57'
  }),
  [TaskQuestionType.PassKey]: () => ({
    value: ''
  })
};

const createFieldPath = (taskId, fieldName) => `${taskId}.${fieldName}`;

const defaultValueByTask = (taskId, user) => {
  const configFn = DEFAULT_VALUE_CONFIGS[taskId];
  const defaultValues = configFn ? configFn(user) : {};

  return { [taskId]: defaultValues };
};

const getAnswersSchema = schemas => {
  const [firstSchema, ...restSchemas] = schemas;

  if (schemas.length > 1) {
    return firstSchema.concat(...restSchemas);
  }

  return firstSchema;
};

const getAnswerSchema = taskId => {
  const schemaConfig = SCHEMA_CONFIGS[taskId];
  let schema;

  if (schemaConfig) {
    schema = yup.object(schemaConfig);
  } else {
    schema = yup.object({});
  }

  return yup.object({ [taskId]: schema });
};

const getFieldError = (errors, taskId, fieldName) => {
  const taskErrors = errors[taskId];
  return taskErrors ? taskErrors[fieldName] : null;
};

const getFieldErrorMessage = (errors, taskId, fieldName) => {
  const error = getFieldError(errors, taskId, fieldName);

  if (error && error.message) {
    return error.message;
  }

  return '';
};

const getFieldValue = (getValues, taskId, fieldName) => {
  const path = createFieldPath(taskId, fieldName);
  return getValues(path);
};

const ContractChallenge = props => {
  const {
    tasks,
    challengeId,
    type,
    loading,
    handleAnswers,
    contract,
    setInvoiceHelp
  } = props;

  const classes = useStyles();

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down(600));
  const currentUser = useContext(UserContext);

  const defaultValues = useMemo(() => {
    let values = {};

    for (const task of tasks) {
      values = { ...values, ...defaultValueByTask(task.id, currentUser) };
    }

    return values;
  }, [tasks, currentUser]);

  const answersSchema = useMemo(() => {
    const schemas = tasks.map(({ id }) => getAnswerSchema(id));

    return getAnswersSchema(schemas);
  }, [tasks]);

  const { handleSubmit, register, getValues, errors, control } = useForm({
    resolver: yupResolver(answersSchema),
    mode: 'onSubmit',
    defaultValues
  });

  const formHelpers = useMemo(
    () => ({
      getFieldPath: (taskId, fieldName) => createFieldPath(taskId, fieldName),
      getFieldValue: (taskId, fieldName) =>
        getFieldValue(getValues, taskId, fieldName),
      getFieldError: (taskId, fieldName) =>
        getFieldError(errors, taskId, fieldName),
      getFieldErrorMessage: (taskId, fieldName) =>
        getFieldErrorMessage(errors, taskId, fieldName)
    }),
    [getValues, errors]
  );

  const getInputLabel = questionId => {
    switch (questionId) {
      case TaskQuestionType.RecentInvoice: {
        const numberText = isSmallScreen ? 'No.' : 'Número';

        return `${numberText} de ${invoiceNumberName}`;
      }
      case TaskQuestionType.PassKey:
        return 'Código de activación';
      case TaskQuestionType.PhoneValidation:
        return 'Validación de teléfono';

      default:
        return '';
    }
  };

  const getTaskTitle = questionId => {
    switch (questionId) {
      case TaskQuestionType.RecentInvoice:
        return 'Validación de factura';
      case TaskQuestionType.PassKey:
        return 'Código de activación';
      case TaskQuestionType.PhoneValidation:
        return 'Validación de teléfono';

      default:
        return 'Reto no válido';
    }
  };

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  const renderChallengeByType = (id, description, metadata) => {
    const inputLabel = getInputLabel(id);
    const taskTitle = getTaskTitle(id);

    switch (type) {
      case ContractChallengeType.Question: {
        return (
          <QuestionChallenge
            key={id}
            id={id}
            description={description}
            label={inputLabel}
            loading={loading}
            setInvoiceHelp={setInvoiceHelp}
            metadata={metadata}
            register={register}
            control={control}
            title={taskTitle}
            formHelpers={formHelpers}
          />
        );
      }
      default:
        return <></>;
    }
  };

  const formatAnswers = useCallback(
    userTasks => {
      const userAnswers = userTasks.map(chall => {
        const { id } = chall;
        const keys = Object.keys(SCHEMA_CONFIGS[id]);

        const answers = {};
        for (const key of keys) {
          answers[key] = formHelpers.getFieldValue(id, key);
        }

        return {
          ...chall,
          answer: answers
        };
      });

      return userAnswers;
    },
    [formHelpers]
  );

  const handleAnswersValidation = useCallback(async () => {
    const userAnswers = formatAnswers(tasks);

    await handleAnswers(userAnswers);
  }, [handleAnswers, formatAnswers, tasks]);

  return (
    <div className={classes.root}>
      {loading && <ModalProgress message="Estamos validando tus respuestas" />}
      <form
        id={`challenge-form-${challengeId}`}
        onSubmit={handleSubmit(handleAnswersValidation)}
      >
        <Typography className={classes.text}>
          Contrato: <strong>{contract.id}</strong>
        </Typography>
        <Typography paragraph className={classes.sectionText}>
          Para continuar, debe responder la(s) siguiente(s) pregunta(s):
        </Typography>
        <ol className={classes.orderedList}>
          {tasks.map(({ id, description, metadata }, index) => (
            <li className={classes.questionContainer} key={id}>
              {renderChallengeByType(id, description, metadata)}
              {tasks.length > 1 && index < tasks.length - 1 && (
                <SimpleDivider />
              )}
            </li>
          ))}
        </ol>
      </form>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(1)
  },
  text: {
    fontSize: 14,
    fontWeight: 500,
    paddingBottom: theme.spacing(),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingBottom: theme.spacing(2),
      paddingRight: 0,
      paddingLeft: 0
    }
  },
  sectionText: {
    margin: theme.spacing(1.5, 0, 5, 0)
  },
  questionContainer: {
    marginBottom: theme.spacing(2)
  },
  questionText: {
    display: 'flex',
    alignItems: 'center'
  },
  accordionTitle: {
    color: theme.palette.text.main,
    fontWeight: 600,
    fontSize: 12
  },
  orderedList: {
    paddingInlineStart: 20
  }
}));

export default ContractChallenge;
